/**
 * How a price is shown.
 *
 * `Act.price` is free text with no currency field anywhere in the schema, so
 * it holds anything a human or a model wrote: "€25 adult", "Free", "€€",
 * "CASH ONLY — beer ~€5–6", "$117 pp (~€107)". A "$$" rating is derived from
 * it by grabbing the first number, which is a guess and sometimes a bad one.
 *
 * The rule this module exists to enforce: **a price never renders as
 * nothing.** When a rating can't be derived — a third of the Croatia trip's
 * prices contain no digit at all — the original text is shown instead. The
 * details card previously dropped the row entirely in that case, which turned
 * "Free" and "Included with the walls ticket" into blank space.
 *
 * The rating stays a rough comparability signal, not a claim of precision.
 * Proper structured price (amount + currency) is a later change; this keeps
 * the display honest until then.
 */

/**
 * A "$".."$$$$" band, or "" when the text carries no number to band.
 *
 * Known limits, kept deliberately visible: the first number in the string
 * wins, so "happy hour 12:00–21:00" reads as 12; and the scale is chosen by
 * substring-matching the category, so "Dinner · Split" misses the meal scale
 * while "Agrotourism · Brač" matches the excursion one on "tour". Currency is
 * not considered at all — $117 and €117 band identically.
 */
export function priceRating(price: string | undefined, category?: string): string {
  if (!price) return "";
  const match = price.match(/[\d.]+/);
  if (!match) return "";
  const amount = parseFloat(match[0]);
  if (!Number.isFinite(amount)) return "";

  const cat = (category || "").toLowerCase();
  const band = (a: number, b: number, c: number) => (amount < a ? "$" : amount < b ? "$$" : amount < c ? "$$$" : "$$$$");

  if (/meal|dining|restaurant|food/.test(cat)) return band(15, 30, 50);
  if (/tour|excursion|cruise|expedition|adventure/.test(cat)) return band(60, 120, 180);
  return band(25, 50, 100);
}

/**
 * What to actually put on screen: the rating when one can be derived,
 * otherwise the price text itself. Never empty when a price exists.
 */
export function priceLabel(price: string | undefined, category?: string): string {
  if (!price) return "";
  return priceRating(price, category) || price.trim();
}

/**
 * Is this activity free? True when the curator flagged it (`badge: "free"`)
 * OR the price copy says so before any amount ("Free", "Free — donations
 * welcome"). One predicate for every surface, so the FREE badge and a "Free"
 * price text can never both render — that duplicate is exactly the bug this
 * replaces.
 */
export function isFree(price: string | undefined, badge?: "book" | "free"): boolean {
  if (badge === "free") return true;
  const first = (price || "").split(/[—·]/)[0];
  return /^[^€$£0-9]*\bfree\b/i.test(first);
}

/**
 * Reservation status is a THREE-state fact, and the third state is the one
 * that matters: we frequently don't know.
 *
 * `badge` is only ever set by the curated extraction or a human editing the
 * activity — the AI surfaces don't populate it — so treating "no badge" as
 * "walk-up" told every traveler on a generated trip that no booking was
 * needed at 40 restaurants nobody had checked. Absence of evidence is not
 * "no reservation required"; it renders as an explicit unknown that invites
 * the human to set it.
 *
 *   "book"    → ⚑ reservation required (and `bookable()` puts it on the
 *               booking checklist as soon as the slot is ✔ Confirmed)
 *   "walkup"  → known to need nothing
 *   "unknown" → nobody has said
 */
export type Reservation = "book" | "walkup" | "unknown";

export function reservationOf(badge?: "book" | "free"): Reservation {
  if (badge === "book") return "book";
  if (badge === "free") return "walkup";
  return "unknown";
}
